/**
 * Version Store for Seamless Updates
 *
 * Enables Facebook/Google-style seamless updates by tracking version mismatches
 * between the running frontend code and the latest deployed frontend version.
 *
 * At build time, a `/version.json` manifest is generated. At runtime, the store
 * periodically fetches `/version.json` and compares against the version baked into
 * the running JS bundle. When a mismatch is detected, the updateAvailable flag is
 * set. On the next navigation, the app performs a full page reload to load the new
 * code, providing a seamless update experience.
 *
 * @see docs/plans/2025-01-11-logout-and-seamless-updates-design.md
 */

import { create } from 'zustand';
import { shallow } from 'zustand/shallow';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VersionState {
  /** Current client version from VITE_BUILD_VERSION at build time */
  currentVersion: string;
  /** Latest deployed version fetched from /version.json */
  deployedVersion: string | null;
  /** True when deployed version differs from client version */
  updateAvailable: boolean;
  /** Timestamp of last version check (for debugging/monitoring) */
  lastChecked: number | null;
}

interface VersionActions {
  /**
   * Fetch /version.json and compare against the running bundle version.
   * Sets updateAvailable to true if version differs from current.
   * Silently catches all errors (network, 404, parse).
   * Skipped entirely when currentVersion === 'development'.
   */
  checkForUpdate: () => Promise<void>;

  /**
   * Start background version checks: setInterval (5 min) + visibilitychange listener.
   * No-op in development mode.
   */
  startBackgroundChecks: () => void;

  /**
   * Stop background version checks. Clears interval and removes listener.
   */
  stopBackgroundChecks: () => void;

  /**
   * Clear the update flag after a reload has occurred.
   * Called when the app initializes to reset state.
   */
  clearUpdateFlag: () => void;

  /**
   * Check if there's a version mismatch requiring reload.
   * Returns true if updateAvailable is true.
   */
  checkVersionMismatch: () => boolean;
}

type VersionStore = VersionState & VersionActions;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Get current version from build environment.
 * Falls back to 'development' when not set (local dev environment).
 */
const getCurrentVersion = (): string => {
  try {
    const buildVersion = import.meta.env.VITE_BUILD_VERSION;

    // Handle undefined, null, or empty string
    if (!buildVersion || typeof buildVersion !== 'string' || buildVersion.trim() === '') {
      return 'development';
    }

    return buildVersion.trim();
  } catch {
    return 'development';
  }
};

/**
 * Minimum interval between version checks (in milliseconds).
 * Prevents excessive fetches if multiple triggers fire close together.
 */
const VERSION_CHECK_THROTTLE_MS = 30_000; // 30 seconds

/**
 * Interval between periodic background version checks.
 */
const BACKGROUND_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Timeout for fetching /version.json.
 */
const FETCH_TIMEOUT_MS = 3_000; // 3 seconds

// ---------------------------------------------------------------------------
// Background check state (module-level, outside Zustand)
// ---------------------------------------------------------------------------

let backgroundInterval: ReturnType<typeof setInterval> | null = null;
let visibilityHandler: (() => void) | null = null;

// ---------------------------------------------------------------------------
// Store Implementation
// ---------------------------------------------------------------------------

export const useVersionStore = create<VersionStore>((set, get) => ({
  // Initial state
  currentVersion: getCurrentVersion(),
  deployedVersion: null,
  updateAvailable: false,
  lastChecked: null,

  checkForUpdate: async () => {
    const state = get();

    // Skip in development mode
    if (state.currentVersion === 'development') {
      return;
    }

    // Throttle: skip if checked too recently
    const now = Date.now();
    if (state.lastChecked && now - state.lastChecked < VERSION_CHECK_THROTTLE_MS) {
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

      const response = await fetch(`/version.json?t=${now}`, {
        signal: controller.signal,
        cache: 'no-store',
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const deployedVersion = typeof data?.version === 'string' ? data.version.trim() : null;

      if (!deployedVersion) {
        return;
      }

      const versionsDiffer = deployedVersion !== state.currentVersion;
      const updateAvailable = versionsDiffer;

      // Log version mismatch for debugging (first detection only)
      if (updateAvailable && !state.updateAvailable) {
        console.info(
          `[VersionStore] Update available: client="${state.currentVersion}", deployed="${deployedVersion}"`
        );
      }

      set({
        deployedVersion,
        updateAvailable,
        lastChecked: now,
      });
    } catch {
      // Silently ignore all errors — version checking should never break the app.
      // This covers network errors, AbortError (timeout), JSON parse errors, etc.
    }
  },

  startBackgroundChecks: () => {
    const state = get();

    // No-op in development mode
    if (state.currentVersion === 'development') {
      return;
    }

    // Prevent duplicate registrations
    if (backgroundInterval) {
      return;
    }

    // Periodic check every 5 minutes
    backgroundInterval = setInterval(() => {
      get().checkForUpdate();
    }, BACKGROUND_CHECK_INTERVAL_MS);

    // Check when tab becomes visible (user returns to the app)
    visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        get().checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);

    // Run an initial check
    get().checkForUpdate();
  },

  stopBackgroundChecks: () => {
    if (backgroundInterval) {
      clearInterval(backgroundInterval);
      backgroundInterval = null;
    }
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler);
      visibilityHandler = null;
    }
  },

  clearUpdateFlag: () => {
    set({
      updateAvailable: false,
      deployedVersion: null,
      lastChecked: null,
    });
  },

  checkVersionMismatch: () => {
    return get().updateAvailable;
  },
}));

// ---------------------------------------------------------------------------
// Selector Hooks (for React components)
// ---------------------------------------------------------------------------

/**
 * Hook to check if an update is available.
 * Use this in navigation components to decide on full reload vs SPA navigation.
 *
 * @example
 * ```tsx
 * const updateAvailable = useUpdateAvailable();
 *
 * const handleNavigation = (path: string) => {
 *   if (updateAvailable) {
 *     window.location.href = path; // Full reload to get new code
 *   } else {
 *     navigate(path); // Normal SPA navigation
 *   }
 * };
 * ```
 */
export const useUpdateAvailable = (): boolean => {
  return useVersionStore((state) => state.updateAvailable);
};

/**
 * Hook to get full version information for debugging or display.
 * Uses shallow comparison to prevent unnecessary re-renders when
 * the selected values haven't actually changed.
 *
 * @example
 * ```tsx
 * const { currentVersion, deployedVersion, updateAvailable, lastChecked } = useVersionInfo();
 * ```
 */
export const useVersionInfo = (): VersionState => {
  return useVersionStore(
    (state) => ({
      currentVersion: state.currentVersion,
      deployedVersion: state.deployedVersion,
      updateAvailable: state.updateAvailable,
      lastChecked: state.lastChecked,
    }),
    shallow
  );
};

// ---------------------------------------------------------------------------
// Non-React Access (for API interceptors and utilities)
// ---------------------------------------------------------------------------

/**
 * Check if update is available outside React components.
 * Use in API interceptors or navigation guards.
 *
 * @example
 * ```ts
 * if (isUpdateAvailable()) {
 *   window.location.href = newPath;
 * }
 * ```
 */
export const isUpdateAvailable = (): boolean => {
  return useVersionStore.getState().updateAvailable;
};

/**
 * Clear update flag outside React components.
 * Call on app initialization after reload.
 */
export const clearUpdateFlag = (): void => {
  useVersionStore.getState().clearUpdateFlag();
};

/**
 * Get version info outside React components.
 * Useful for debugging, logging, and metrics.
 *
 * @example
 * ```ts
 * const info = getVersionInfo();
 * console.log(`Running ${info.currentVersion}, deployed ${info.deployedVersion}`);
 * ```
 */
export const getVersionInfo = (): VersionState => {
  const state = useVersionStore.getState();
  return {
    currentVersion: state.currentVersion,
    deployedVersion: state.deployedVersion,
    updateAvailable: state.updateAvailable,
    lastChecked: state.lastChecked,
  };
};

/**
 * Check for version mismatch outside React components.
 * Delegates to the store action for consistency.
 */
export const checkVersionMismatch = (): boolean => {
  return useVersionStore.getState().checkVersionMismatch();
};
